import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { ScrollText, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { t } from "../lib/i18n";

export const MandateDialog = ({ sig, open, onOpenChange, onAccepted }) => {
  const [data, setData] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !sig) return;
    setData(null);
    setAccepted(false);
    api.get(`/mandate/for-signature/${sig.signature_id}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error(t("No se pudo cargar el mandato")));
  }, [open, sig]);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/nda/${sig.signature_id}/approve`, { accept_mandate: true });
      onAccepted(r.data);
      onOpenChange(false);
      toast.success(`Mandato aceptado · ${sig.signer_name} ya puede ver tu IM`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error aceptando el mandato");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl p-0 overflow-hidden" data-testid="mandate-dialog">
        <DialogHeader className="bg-[#1E3A5F] text-bone p-6 space-y-2">
          <span className="font-mono-label text-gold flex items-center gap-2">
            <ScrollText className="w-3 h-3" />{" "}{t("Mandato de Asesoramiento M&A · Relevo")}
          </span>
          <DialogTitle className="font-serif-display text-2xl md:text-3xl text-bone font-normal">
            {data?.already_has_mandate ? t("Incorporar comprador protegido") : t("Acepta el mandato para aprobar el acceso")}
          </DialogTitle>
          <DialogDescription className="text-bone/80">
            {t("Aprobar el NDA de")}{" "}<span className="font-semibold text-bone">{sig?.signer_name}</span>{" "}{t("incorpora a este comprador como Comprador Protegido bajo tu Mandato Relevo para")}{" "}<span className="font-semibold text-bone">{data?.company_name || sig?.company_name}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-5">
          <div
            className="hairline bg-card p-5 max-h-[300px] overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed"
            data-testid="mandate-text-content"
          >
            {data ? data.text : t("Cargando mandato…")}
          </div>
          {data && (
            <div className="flex items-center justify-between font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>
              <span>{t("Versión:")}{" "}{data.document_version}</span>
              <span data-testid="mandate-doc-hash">{t("Hash:")}{" "}{data.document_hash.slice(0, 16)}…</span>
            </div>
          )}

          <div className="hairline bg-bone p-5 space-y-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-[#2E6F80] flex-shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed" data-testid="mandate-fee-summary">
                <span className="font-semibold">{t("No pagarás nada ahora.")}</span>{" "}{t("Relevo cobrará únicamente si completas una operación con un Comprador Protegido. Honorarios:")}{" "}<span className="font-semibold text-[#1E3A5F]">3%</span>.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="mandate-accept"
                checked={accepted}
                onCheckedChange={(v) => setAccepted(Boolean(v))}
                className="rounded-none mt-1"
                data-testid="mandate-accept-checkbox"
              />
              <label htmlFor="mandate-accept" className="text-sm cursor-pointer select-none leading-relaxed">
                {data?.already_has_mandate
                  ? data.incorporation_sentence
                  : t("He leído y acepto el Mandato de Asesoramiento M&A de Relevo.")}
              </label>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" className="rounded-none h-10" onClick={() => onOpenChange(false)} data-testid="mandate-cancel-btn">
              {t("Cancelar")}
            </Button>
            <Button
              onClick={confirm}
              disabled={!accepted || !data || busy}
              className="rounded-none h-10 px-6 text-bone"
              style={{ backgroundColor: "#2E6F80" }}
              data-testid="mandate-confirm-btn"
            >
              {busy ? t("Registrando…") : t("Aceptar mandato y aprobar acceso")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
